import { useState } from 'react'
import { Tabs, Form, DatePicker, Checkbox, Select, Button, Typography, Row, Col, Table, Tag, Space, List } from 'antd'
import { CalendarOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toastSuccess } from '../../utils/toast'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

export default function AuditTrailExport() {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('create')

  const exportJobsData = [
    {
      key: '1',
      type: 'CSV Export',
      status: 'completed',
      startTime: 'Aug 13, 2025, 09:29 AM',
      duration: '6s',
      records: '4,941 records',
      size: '2.9 MB'
    }
  ]

  const recentEventsData = [
    {
      key: '1',
      eventType: 'user login',
      entityType: 'user',
      timestamp: 'Aug 12, 2025, 08:42 PM',
      title: 'User logged in successfully',
      user: 'admin@paygate.com',
      ip: '192.168.1.100',
      entity: 'user_123'
    },
    {
      key: '2',
      eventType: 'transaction processed',
      entityType: 'transaction',
      timestamp: 'Aug 12, 2025, 07:42 PM',
      title: 'Transaction processed successfully',
      user: 'system@paygate.com',
      ip: '10.0.0.50',
      entity: 'txn_456',
      changes: [
        'status: pending → completed',
        'processedAt: null → 2025-08-12T15:42:05.083Z'
      ]
    },
    {
      key: '3',
      eventType: 'partner updated',
      entityType: 'partner',
      timestamp: 'Aug 12, 2025, 06:42 PM',
      title: 'Partner configuration updated',
      user: 'manager@paygate.com',
      ip: '192.168.1.105',
      entity: 'partner_789',
      changes: [
        'apiEndpoint: https://old.api.com → https://new.api.com',
        'rateLimit: 1000 → 2000'
      ]
    }
  ]

  const exportJobsColumns = [
    {
      title: 'Export Job',
      dataIndex: 'type',
      key: 'type',
      render: (text: string, record: any) => (
        <div>
          <Space style={{ fontWeight: 500, marginBottom: 8 }}>
            {text}
            <Tag color={record.status === 'completed' ? 'green' : record.status === 'running' ? 'blue' : 'orange'}>
              {record.status}
            </Tag>
          </Space>
          <div style={{ color: '#666', fontSize: '12px' }}>
            Started: {record.startTime} • Duration: {record.duration}
          </div>
        </div>
      )
    },
    {
      title: 'Results',
      key: 'results',
      render: (record: any) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignContent: 'center', gap: 20 }}>
          <div>{record.records} {record.size}</div>
          <Button
            style={{ borderRadius: 8, border: '1px solid #d9d9d9', marginTop: -6 }}
            type="link"
            icon={<DownloadOutlined />}
          >
          </Button>
        </div>
      )
    }
  ]

  const eventTypeOptions = [
    { label: 'user login', value: 'user_login' },
    { label: 'user logout', value: 'user_logout' },
    { label: 'user created', value: 'user_created' },
    { label: 'user updated', value: 'user_updated' },
    { label: 'transaction processed', value: 'transaction_processed' },
    { label: 'transaction failed', value: 'transaction_failed' },
    { label: 'transaction refunded', value: 'transaction_refunded' },
    { label: 'partner created', value: 'partner_created' },
    { label: 'partner updated', value: 'partner_updated' },
    { label: 'partner deleted', value: 'partner_deleted' },
    { label: 'api key_created', value: 'api_key_created' },
    { label: 'api key_revoked', value: 'api_key_revoked' },
    { label: 'security alert', value: 'security_alert' },
    { label: 'system config_changed', value: 'system_config_changed' }
  ]

  const entityTypeOptions = [
    { label: 'user', value: 'user' },
    { label: 'transaction', value: 'transaction' },
    { label: 'partner', value: 'partner' },
    { label: 'api_key', value: 'api_key' },
    { label: 'webhook', value: 'webhook' },
    { label: 'system', value: 'system' }
  ]

  const handleExport = () => {
    toastSuccess('Export started successfully')
  }

  const CreateExportTab = () => (
    <div style={{ margin: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4}>Export Configuration</Title>
        <Text type="secondary">Configure your audit trail export parameters</Text>
      </div>

      <Form form={form} layout="vertical" onFinish={handleExport}>
        <Form.Item
          label="Date Range"
          name="date"
          initialValue={[dayjs('2025-08-01'), dayjs('2025-08-31')]}
        >
          <RangePicker
            style={{ width: 400 }}
            format="YYYY-MM-DD"
            suffixIcon={<CalendarOutlined />}
          />
        </Form.Item>

        <div style={{ marginBottom: 20 }}>
          <Title level={5}>Event Types</Title>
          <Text type="secondary">Select specific event types to include</Text>
          <Form.Item name="eventTypes" style={{ marginTop: 12 }}>
            <Checkbox.Group>
              <Row gutter={[12, 12]}>
                {eventTypeOptions.map((option, index) => (
                  <Col span={8} key={option.value}>
                    <Checkbox value={option.value}>{option.label}</Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </div>

        <div style={{ marginBottom: 20 }}>
          <Title level={5}>Entity Types</Title>
          <Text type="secondary">Select entity types to include</Text>
          <Form.Item name="entityTypes" style={{ marginTop: 12 }}>
            <Checkbox.Group>
              <Row gutter={[12, 12]}>
                {entityTypeOptions.map((option, index) => (
                  <Col span={8} key={option.value}>
                    <Checkbox value={option.value}>{option.label}</Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Form.Item label="Export Format" name="exportFormat" initialValue="csv">
            <Select
              style={{ width: 200 }}
              options={[
                { label: 'CSV (Comma Separated)', value: 'csv' },
                { label: 'JSON', value: 'json' },
                { label: 'Excel', value: 'excel' }
              ]}
            />
          </Form.Item>
          <div>
            <Title level={5}>Include Options</Title>
            <Form.Item name="includeOptions" style={{ marginTop: 16 }}>
              <Checkbox.Group>
                <Space direction="vertical">
                  <Checkbox checked value="metadata">Include metadata</Checkbox>
                  <Checkbox checked value="fieldChanges">Include field changes</Checkbox>
                </Space>
              </Checkbox.Group>
            </Form.Item>
          </div>
          <div></div>
        </div>

        <Button
          type="primary"
          htmlType="submit"
          icon={<DownloadOutlined />}
        >
          Start Export
        </Button>
      </Form>
    </div >
  )

  const ExportJobsTab = () => (
    <div style={{ margin: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4}>Export Jobs</Title>
        <Text type="secondary">Track the status of your export jobs</Text>
      </div>

      <Table
        columns={exportJobsColumns}
        dataSource={exportJobsData}
        pagination={false}
        showHeader={false}
      />
    </div>
  )

  const RecentEventsTab = () => (
    <div style={{ margin: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4}>Recent Audit Events</Title>
        <Text type="secondary">Latest audit trail events in the system</Text>
      </div>

      <List
        dataSource={recentEventsData}
        renderItem={(item) => (
          <List.Item
            style={{
              padding: '16px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
            actions={[
              <Button
                type="text"
                style={{ borderRadius: 8, border: '1px solid #d9d9d9' }}
                icon={<EyeOutlined />}
              />
            ]}
          >
            <List.Item.Meta
              description={
                <div style={{ marginBottom: 20 }}>
                  <Space style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Tag>{item.eventType}</Tag>
                      <Tag>{item.entityType}</Tag>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {item.timestamp}
                      </Text>
                    </div>
                  </Space>
                  <div>
                    <Text strong>{item.title}</Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">
                      User: {item.user} • IP: {item.ip} • Entity: {item.entity}
                    </Text>
                  </div>
                  {item.changes && (
                    <div>
                      <Text strong style={{ fontSize: '12px' }}>Changes:</Text>
                      <div style={{ marginLeft: 16, fontSize: '12px' }}>
                        {item.changes.map((change, index) => (
                          <div key={index} style={{ fontFamily: 'monospace', color: '#666' }}>
                            {change}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              }
            />
          </List.Item>
        )}
      />
    </div>
  )

  const items = [
    {
      key: 'create',
      label: 'Create Export',
      children: <CreateExportTab />
    },
    {
      key: 'jobs',
      label: 'Export Jobs',
      children: <ExportJobsTab />
    },
    {
      key: 'events',
      label: 'Recent Events',
      children: <RecentEventsTab />
    }
  ]

  return (
    <div>
      <Tabs
        type='card'
        activeKey={activeTab}
        onChange={setActiveTab}
        items={items}
      />
    </div>
  )
}
